import { Alert, Card, Grid, TextField } from '@mui/material';
import DashboardNavbar from 'components/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import { useState } from 'react';
import { getBlackjackV2Tables } from 'services/casino-ops/get-blackjack-v2-tables';
import { mintDevGameModeToken } from 'services/casino-ops/mint-dev-game-mode-token';
import { resetBlackjackV2Table } from 'services/casino-ops/reset-blackjack-v2-table';
import { kickAllSessions } from 'services/queue/kick-all-sessions';

export default function CasinoOps() {
  const [kicking, setKicking] = useState(false);
  const [resettingTables, setResettingTables] = useState(false);
  const [mintingDevToken, setMintingDevToken] = useState(false);
  const [copyingToken, setCopyingToken] = useState(false);
  const [error, setError] = useState('');
  const [kickResult, setKickResult] = useState(null);
  const [kickResultMessage, setKickResultMessage] = useState('');
  const [resetTablesResult, setResetTablesResult] = useState(null);
  const [mintedDevToken, setMintedDevToken] = useState(null);
  const [devTokenSessionId, setDevTokenSessionId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const clearFeedback = () => {
    setError('');
    setSuccessMessage('');
  };

  const onKickAll = async () => {
    const confirmed = window.confirm('Kick every active casino user and remove them from all tables?');
    if (!confirmed) {
      return;
    }

    setKicking(true);
    clearFeedback();
    setKickResult(null);
    setKickResultMessage('');

    const res = await kickAllSessions('Kicked from backoffice');
    if (res?.status === 200 || res?.status === 201) {
      setKickResult(res.data?.data || {});
      setKickResultMessage(res.data?.message || '');
    } else {
      setError(res?.response?.data?.message || 'Failed to kick active casino users');
    }

    setKicking(false);
  };

  const getAllBlackjackV2Tables = async () => {
    const collectedTables = [];
    let page = 1;
    let totalPages = 1;

    do {
      const res = await getBlackjackV2Tables({
        page,
        limit: 100,
        sortBy: 'tableId:ASC'
      });

      if (res?.status !== 200) {
        throw new Error(res?.response?.data?.message || 'Failed to fetch Blackjack V2 tables');
      }

      const pageTables = Array.isArray(res.data?.data) ? res.data.data : [];
      collectedTables.push(...pageTables);
      totalPages = res.data?.meta?.totalPages || 1;
      page += 1;
    } while (page <= totalPages);

    return collectedTables;
  };

  const onResetAllTables = async () => {
    const confirmed = window.confirm(
      'Reset all Blackjack V2 tables and remove active players from those tables?'
    );
    if (!confirmed) {
      return;
    }

    setResettingTables(true);
    clearFeedback();
    setResetTablesResult(null);

    try {
      const tables = await getAllBlackjackV2Tables();

      if (tables.length === 0) {
        setSuccessMessage('No Blackjack V2 tables found to reset');
        setResetTablesResult({
          totalTables: 0,
          resetTables: 0,
          failedTables: []
        });
        return;
      }

      const results = [];

      for (const table of tables) {
        const tableId = table?.tableId;
        if (!tableId) {
          results.push({
            tableId: 'Unknown table',
            success: false,
            message: 'Missing tableId in table list response'
          });
          continue;
        }

        const res = await resetBlackjackV2Table(tableId, true);
        results.push({
          tableId,
          success: res?.status === 200 || res?.status === 201,
          message: res?.response?.data?.message || ''
        });
      }

      const failedTables = results.filter((entry) => !entry.success);
      const resetCount = results.length - failedTables.length;

      setResetTablesResult({
        totalTables: results.length,
        resetTables: resetCount,
        failedTables
      });

      if (failedTables.length > 0) {
        setError(
          `Reset ${resetCount} of ${results.length} Blackjack V2 table(s). ${failedTables.length} failed.`
        );
      } else {
        setSuccessMessage(`Reset all ${results.length} Blackjack V2 table(s)`);
      }
    } catch (err) {
      setError(err?.message || 'Failed to reset Blackjack V2 tables');
    } finally {
      setResettingTables(false);
    }
  };

  const onMintDevToken = async () => {
    setMintingDevToken(true);
    clearFeedback();
    setMintedDevToken(null);

    const normalizedSessionId = devTokenSessionId.trim();
    const res = await mintDevGameModeToken(normalizedSessionId || undefined);

    if (res?.status === 200 || res?.status === 201) {
      setMintedDevToken(res.data || null);
      setSuccessMessage('Dev game mode token minted');
    } else {
      setError(res?.response?.data?.message || 'Failed to mint dev game mode token');
    }

    setMintingDevToken(false);
  };

  const onCopyDevToken = async () => {
    if (!mintedDevToken?.gameModeToken || copyingToken) {
      return;
    }

    setCopyingToken(true);
    clearFeedback();

    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable');
      }

      await navigator.clipboard.writeText(mintedDevToken.gameModeToken);
      setSuccessMessage('Dev game mode token copied to clipboard');
    } catch (err) {
      setError('Failed to copy token to clipboard. Copy it manually from the token field.');
    } finally {
      setCopyingToken(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          {error && (
            <Grid item xs={12}>
              <Alert severity='error'>{error}</Alert>
            </Grid>
          )}

          {successMessage && (
            <Grid item xs={12}>
              <Alert severity='success'>{successMessage}</Alert>
            </Grid>
          )}

          <Grid item xs={12} md={8} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant='h5' fontWeight='bold' mb={2}>
                  Casino Ops
                </MDTypography>

                {kickResult && (
                  <MDBox mb={2}>
                    <Alert severity={kickResult.failures?.length ? 'warning' : 'success'}>
                      {kickResultMessage ? `${kickResultMessage}. ` : ''}
                      Kicked {kickResult.usersKicked || 0} user(s), updated {kickResult.sessionsKicked || 0} active session(s)
                      {kickResult.failures?.length ? `, ${kickResult.failures.length} failed` : ''}.
                    </Alert>
                  </MDBox>
                )}

                <MDTypography variant='h6' fontWeight='medium' mb={1}>
                  Active Session Controls
                </MDTypography>

                <MDBox display='flex' alignItems='center' gap={2} flexWrap='wrap'>
                  <MDButton variant='gradient' color='error' onClick={onKickAll} disabled={kicking}>
                    {kicking ? 'Kicking...' : 'Kick All Casino Users'}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={8} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant='h6' fontWeight='medium' mb={1}>
                  Mint Dev Game Mode Token
                </MDTypography>

                <MDTypography variant='button' color='text' display='block' mb={2}>
                  Optional session scope. When set, child game actor tokens must use the same session.
                </MDTypography>

                <MDBox mb={2}>
                  <TextField
                    fullWidth
                    label='Session ID'
                    placeholder='Optional session ID'
                    value={devTokenSessionId}
                    onChange={(event) => setDevTokenSessionId(event.target.value)}
                    disabled={mintingDevToken}
                  />
                </MDBox>

                <MDBox display='flex' alignItems='center' gap={2} flexWrap='wrap' mb={mintedDevToken?.gameModeToken ? 2 : 0}>
                  <MDButton
                    variant='gradient'
                    color='info'
                    onClick={onMintDevToken}
                    disabled={mintingDevToken}
                  >
                    {mintingDevToken ? 'Minting...' : 'Mint Dev Token'}
                  </MDButton>
                  <MDButton
                    variant='outlined'
                    color='info'
                    onClick={onCopyDevToken}
                    disabled={!mintedDevToken?.gameModeToken || copyingToken}
                  >
                    {copyingToken ? 'Copying...' : 'Copy Token'}
                  </MDButton>
                </MDBox>

                {mintedDevToken?.gameModeToken && (
                  <MDBox>
                    <MDTypography variant='button' fontWeight='medium' display='block'>
                      Session: {mintedDevToken.sessionId || 'Unscoped'}
                    </MDTypography>
                    <MDTypography variant='button' fontWeight='medium' display='block' mb={2}>
                      Expires:{' '}
                      {mintedDevToken.expiresAt
                        ? new Date(mintedDevToken.expiresAt).toLocaleString()
                        : 'N/A'}
                    </MDTypography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label='Token'
                      value={mintedDevToken.gameModeToken}
                      InputProps={{ readOnly: true }}
                    />
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={8} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant='h6' fontWeight='medium' mb={1}>
                  Reset All Blackjack V2 Tables
                </MDTypography>

                <MDTypography variant='button' color='text' display='block' mb={2}>
                  Performs the admin `reset-table` action against every Blackjack V2 table and removes players.
                </MDTypography>

                <MDBox display='flex' alignItems='center' gap={2} flexWrap='wrap' mb={resetTablesResult ? 2 : 0}>
                  <MDButton
                    variant='gradient'
                    color='warning'
                    onClick={onResetAllTables}
                    disabled={resettingTables}
                  >
                    {resettingTables ? 'Resetting...' : 'Reset All Tables'}
                  </MDButton>
                </MDBox>

                {resetTablesResult && (
                  <MDBox>
                    <Alert
                      severity={resetTablesResult.failedTables.length > 0 ? 'warning' : 'success'}
                    >
                      Reset {resetTablesResult.resetTables || 0} of {resetTablesResult.totalTables || 0} table(s)
                      {resetTablesResult.failedTables.length > 0
                        ? `, ${resetTablesResult.failedTables.length} failed`
                        : ''}
                      .
                    </Alert>

                    {resetTablesResult.failedTables.length > 0 && (
                      <MDBox mt={2}>
                        <MDTypography variant='button' fontWeight='medium' display='block' mb={1}>
                          Failed tables
                        </MDTypography>
                        {resetTablesResult.failedTables.map((table) => (
                          <MDTypography
                            key={table.tableId}
                            variant='caption'
                            color='text'
                            display='block'
                            mb={0.5}
                          >
                            {table.tableId}: {table.message || 'Reset request failed'}
                          </MDTypography>
                        ))}
                      </MDBox>
                    )}
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
